import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { Box, Grid, Typography } from '@mui/material';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { Table } from 'components/molecules/Table';
import { CardList } from 'components/molecules/CardList';
import { Elabels, raportTableColumns, mapRaportToDataSource } from '../../helpers';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { IReportFormValue } from 'models/cases';
import { IntelligenceReportTableCard } from './IntelligenceReportTableCard';
import { EIconTypes } from 'constants/Icons';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { ERouteLinks } from 'models/route';
import { useNavigate, useParams } from 'react-router-dom';
import { EPerPages, ISortOptions } from 'models/table';
import { useCase } from '../../hooks';
import { Pagination } from 'components/molecules/Pagination';
import { Loader } from 'components/atoms/Loader';
import { FormModal } from 'components/molecules/Modal';
import { useQuery } from 'hooks/useQuery';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useFormSchema from './schemaValidation';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { Alert } from 'components/atoms/Alert';
import { FormSelect } from 'components/molecules/FormSelect';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { EDictionaryTypes } from 'models/dictionary';
import { useConfigurationLicence } from 'pages/LicenceConfigurationPage/hooks';
import { useLang } from 'models/langContext';
import { FormInput } from 'components/molecules/FormInput';

interface IIntelligenceReport {
  isFromLE: boolean;
  showAdd: boolean;
  isReadOnly?: boolean;
}

const initialFormValues: IReportFormValue = {
  sourceSystemUniqueId: '',
  instructions: '',
};

const IntelligenceReport = ({ isFromLE, showAdd, isReadOnly }: IIntelligenceReport) => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { id = '', caseId = '' } = useParams<{ id: string; caseId: string }>();
  const query = useQuery();
  const type: string | null = query.get('type')?.trim() || null;
  const { longDateFormat, getProperties } = useGlobalProperty();
  const { isDesktop } = useThemeBreakpoint();
  const { clearAlert, modalAlert } = useAlert();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<EPerPages>(EPerPages.perPage25);
  const { getReportsData, paginatorReports, reports, deleteReport, addReport } = useCase();
  const [sort, setSort] = useState<ISortOptions | null>(null);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [addIntelligenceReport, setAddIntelligenceReport] = useState(false);
  const [isSubmitLoading, setSubmitLoading] = useState(false);
  const { schema } = useFormSchema();
  const { selectedLanguage } = useLang();
  const { loadDictionaries, intelligenceReportSourceSystem } = useConfigurationLicence();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<IReportFormValue>({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<IReportFormValue>,
  });

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([EDictionaryTypes.intelligenceReportSourceSystem]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    getProperties();
  }, []);

  useEffect(() => {
    (async () => {
      setIsPaginationLoading(true);
      if (!reports.length) {
        setIsLoading(true);
      }
      await getReportsData(caseId, currentPage, perPage, sort);
      setIsLoading(false);
      setIsPaginationLoading(false);
    })();
  }, [currentPage, perPage, sort]);

  const reloadCases = async () => {
    await getReportsData(caseId, currentPage, perPage, sort);
  };

  const viewAction = (reportId: string) => {
    isFromLE
      ? navigate(
          `${ERouteLinks.LegalEntityCaseIntelligenceReportPage.replace(':id', id)
            .replace(':caseId', caseId)
            .replace(':reportId', reportId)}?type=${type}`,
        )
      : navigate(
          ERouteLinks.CaseIntelligenceReportPage.replace(':caseId', caseId).replace(
            ':reportId',
            reportId,
          ),
        );
  };
  const editAction = (reportId: string) => {
    isFromLE
      ? navigate(
          `${ERouteLinks.LegalEntityCaseIntelligenceReportEditPage.replace(':id', id)
            .replace(':caseId', caseId)
            .replace(':reportId', reportId)}?type=${type}`,
        )
      : navigate(
          ERouteLinks.CaseIntelligenceReportEditPage.replace(':caseId', caseId).replace(
            ':reportId',
            reportId,
          ),
        );
  };
  const deleteAction = (id: string) => {
    setDeleteId(id);
  };

  const handleDeleteReport = async () => {
    if (deleteId) {
      const res = await deleteReport(deleteId);
      if (res) {
        if (currentPage === 1) {
          reloadCases();
        } else {
          setCurrentPage(1);
        }
        setDeleteId(null);
      }
    }
  };
  const onSubmit = async (data: IReportFormValue) => {
    if (isSubmitLoading) return;
    setSubmitLoading(true);
    try {
      const res = await addReport(caseId, data);
      if (res) {
        reset(initialFormValues);
        setAddIntelligenceReport(false);
        clearAlert(ActionTypes.CLEAR_MODAL_ALERT);
        await reloadCases();
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const navigateAddReportResult = (reportId: string) => {
    isFromLE
      ? navigate(
          `${ERouteLinks.LegalEntityAddIntelligenceReportResult.replace(':id', id)
            .replace(':caseId', caseId)
            .replace(':reportId', reportId)}?type=${type}`,
        )
      : navigate(
          ERouteLinks.AddIntelligenceReportResult.replace(':caseId', caseId).replace(
            ':reportId',
            reportId,
          ),
        );
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormModal
          open={addIntelligenceReport}
          onClickClose={() => setAddIntelligenceReport(false)}
          onClickApprove={handleSubmit(onSubmit)}
          title={t(Elabels.addIntelligenceReport)}
          titleLeft
        >
          <Grid sx={{ gap: 4, mb: 2.5 }}>
            <Grid size={{ xs: 12 }}>
              <FormSelect
                options={mapDictionaryToOptionProp(intelligenceReportSourceSystem)}
                setValue={setValue}
                name="sourceSystemUniqueId"
                label={t(Elabels.sourceSystem)}
                control={control}
                errors={errors}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormInput
                name="instructions"
                label={t('licence.intelligenceReport.instructions.label')}
                multiline
                setValue={setValue}
                minRows={6}
                control={control}
                errors={errors}
                required
                charsLimit={255}
                showCounter
              />
            </Grid>
          </Grid>
          {modalAlert && (
            <Grid size={{ xs: 12 }}>
              <Box>
                <Alert text={modalAlert.text} variant={modalAlert.variant} />
              </Box>
            </Grid>
          )}
        </FormModal>
      </form>
      <FormModal
        open={!!deleteId}
        onClickClose={() => setDeleteId(null)}
        onClickApprove={handleDeleteReport}
        approveButtonLabel="general.yes.label"
        cancelButtonLabel="general.no.label"
        title={t('caseManagement.cases.deleteReport.label')}
        titleLeft
        text={t('caseManagement.cases.areYouSureDeleteReport.text')}
      />

      <Grid container sx={{ gap: '20px' }}>
        <Grid
          container
          size={{ xs: 12 }}
          columnSpacing={1}
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            rowGap: 1,
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            pl: 2,
          }}
        >
          <Typography variant="body2">{t('caseManagement.cases.report.text')}</Typography>
          {showAdd && (
            <Grid size={{ xs: 12, md: 'auto' }}>
              <Button
                label={t(Elabels.addIntelligenceReport)}
                icon={EIconTypes.plus}
                variant={EButtonVariants.contained}
                onClick={() => setAddIntelligenceReport(true)}
                size={EButtonSizes.medium}
                id="intelligence_report_add-button"
                fullWidth
              />
            </Grid>
          )}
        </Grid>

        <Grid size={{ xs: 12 }}>
          {isLoading ? (
            <Loader isLoading />
          ) : (
            <>
              {isDesktop ? (
                <Table
                  columns={raportTableColumns({
                    viewAction,
                    deleteAction,
                    addReportResult: navigateAddReportResult,
                    isReadOnly,
                    showAdd,
                  })}
                  dataSource={mapRaportToDataSource(reports, longDateFormat, t)}
                  onSort={setSort}
                  id="intelligence_report_list-table"
                />
              ) : (
                <CardList
                  items={mapRaportToDataSource(reports, longDateFormat, t)}
                  render={(data, index) => (
                    <IntelligenceReportTableCard
                      data={data}
                      viewAction={viewAction}
                      deleteAction={deleteAction}
                      addReportResult={navigateAddReportResult}
                      isReadOnly={isReadOnly}
                      showAdd={showAdd}
                      key={index}
                    />
                  )}
                />
              )}
              {paginatorReports && (
                <Pagination
                  count={paginatorReports.totalElements}
                  onChangePage={(page) => setCurrentPage(page)}
                  current={currentPage}
                  perPage={perPage}
                  onChangePerPage={(value) => setPerPage(value)}
                  isLoading={isPaginationLoading}
                />
              )}
            </>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default IntelligenceReport;
